// src/pages/alerts/OwnerAlertTemplates.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Paper, Typography, Button, IconButton, Tooltip,
  TextField, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, InputLabel, FormControl, Snackbar, Alert
} from '@mui/material';
import { Add, Edit, Refresh, Visibility, CheckCircle, Block } from '@mui/icons-material';
import axios from 'axios';

const apiUrl = process.env.REACT_APP_API_URL;
const CHANNELS = ['InApp', 'Email', 'SMS', 'WhatsApp'];

const useSnack = () => {
  const [snack, setSnack] = useState({open:false,msg:'',sev:'success'});
  const show = useCallback((msg, sev='success') => setSnack({open:true,msg,sev}), []);
  const onClose = () => setSnack(s => ({...s, open:false}));
  return { snack, show, onClose };
};

const applyVars = (s, vars) =>
  String(s || '')
    .replace(/\{\{horse_name\}\}/g, vars.horse_name || '')
    .replace(/\{\{procedures\}\}/g, vars.procedures || '')
    .replace(/\{\{date\}\}/g, vars.date || '')
    .replace(/\{\{notes\}\}/g, vars.notes || '');

export default function OwnerAlertTemplates() {
  const { snack, show, onClose } = useSnack();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const sampleVars = useMemo(() => ({
    horse_name: 'دعاء',
    procedures: 'تنظيف/قلع سن',
    date: new Date().toISOString().slice(0,10),
    notes: 'لا توجد ملاحظات'
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${apiUrl}/Alerts/templates`, { params: { q }});
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      show('فشل تحميل القوالب', 'error');
    } finally {
      setLoading(false);
    }
  }, [q, show]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing({
      template_key: '',
      title_ar: '',
      body_ar: '',
      title_en: '',
      body_en: '',
      channels: ['InApp'],
      is_active: 1
    });
    setDlgOpen(true);
  };
  const openEdit = (row) => {
    setEditing({
      ...row,
      channels: String(row.channels_csv || 'InApp').split(',').filter(Boolean)
    });
    setDlgOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        template_key: editing.template_key,
        title_ar: editing.title_ar,
        body_ar: editing.body_ar,
        title_en: editing.title_en,
        body_en: editing.body_en,
        channels: editing.channels,
        is_active: editing.is_active ? 1 : 0
      };
      if (editing.template_id) {
        await axios.put(`${apiUrl}/Alerts/templates/${editing.template_id}`, payload);
        show('تم التعديل');
      } else {
        await axios.post(`${apiUrl}/Alerts/templates`, payload);
        show('تم الإنشاء');
      }
      setDlgOpen(false);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'فشل الحفظ';
      show(msg, 'error');
    }
  };

  const toggleActive = async (row) => {
    try {
      await axios.patch(`${apiUrl}/Alerts/templates/${row.template_id}/toggle`);
      show(row.is_active ? 'تم التعطيل' : 'تم التفعيل');
      await load();
    } catch {
      show('تعذر تغيير الحالة', 'error');
    }
  };

  return (
    <Box sx={{ p: { xs:2, md:3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb:2 }}>
        <Typography variant="h5">قوالب رسائل المالك</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="تحديث">
            <span>
              <IconButton onClick={load} disabled={loading}><Refresh/></IconButton>
            </span>
          </Tooltip>
          <Button variant="contained" startIcon={<Add/>} onClick={openCreate}>
            قالب جديد
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p:2, mb:2 }}>
        <TextField
          label="بحث"
          size="small"
          value={q}
          onChange={(e)=>setQ(e.target.value)}
          onKeyDown={(e)=>{ if (e.key==='Enter') load(); }}
          sx={{ width: 380, maxWidth: '100%' }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>المفتاح</TableCell>
              <TableCell>القنوات</TableCell>
              <TableCell>العنوان (ع)</TableCell>
              <TableCell>العنوان (En)</TableCell>
              <TableCell>الحالة</TableCell>
              <TableCell align="right">إجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.template_id}>
                <TableCell>{i+1}</TableCell>
                <TableCell>{r.template_key}</TableCell>
                <TableCell>
                  {String(r.channels_csv || '').split(',').filter(Boolean).map((c) => (
                    <Chip key={c} size="small" label={c} sx={{ mr: .5 }}/>
                  ))}
                </TableCell>
                <TableCell sx={{ maxWidth: 220, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{r.title_ar || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 220, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{r.title_en || '—'}</TableCell>
                <TableCell>
                  {r.is_active ? <Chip size="small" color="success" label="مفعّل"/> : <Chip size="small" color="default" label="موقّف"/>}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="معاينة">
                    <span>
                      <IconButton onClick={() => openEdit(r)}><Visibility/></IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="تعديل">
                    <span>
                      <IconButton onClick={() => openEdit(r)}><Edit/></IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={r.is_active ? 'تعطيل' : 'تفعيل'}>
                    <span>
                      <IconButton onClick={() => toggleActive(r)}>
                        {r.is_active ? <Block/> : <CheckCircle/>}
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && !loading && (
              <TableRow><TableCell colSpan={7} align="center">لا توجد بيانات</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog: create/edit */}
      <Dialog open={dlgOpen} onClose={()=>setDlgOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing?.template_id ? 'تعديل القالب' : 'قالب جديد'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt:1 }}>
            <TextField
              label="المفتاح (template_key)"
              value={editing?.template_key || ''}
              onChange={(e)=>setEditing(s=>({...s, template_key: e.target.value}))}
              disabled={!!editing?.template_id}
              helperText="مثال: PROCEDURE_PERFORMED | يجب أن يكون فريدًا"
              required
            />

            <FormControl>
              <InputLabel>القنوات</InputLabel>
              <Select
                multiple
                label="القنوات"
                value={editing?.channels || []}
                onChange={(e)=>setEditing(s=>({...s, channels: e.target.value}))}
                renderValue={(sel)=>sel.join(', ')}
              >
                {CHANNELS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>

            <Typography variant="subtitle2">العربية</Typography>
            <TextField
              label="العنوان (ع)"
              value={editing?.title_ar || ''}
              onChange={(e)=>setEditing(s=>({...s, title_ar: e.target.value}))}
            />
            <TextField
              label="النص (ع) — يدعم متغيرات: {{horse_name}} {{procedures}} {{date}} {{notes}}"
              value={editing?.body_ar || ''}
              onChange={(e)=>setEditing(s=>({...s, body_ar: e.target.value}))}
              multiline minRows={4}
            />

            <Typography variant="subtitle2">English</Typography>
            <TextField
              label="Title (En)"
              value={editing?.title_en || ''}
              onChange={(e)=>setEditing(s=>({...s, title_en: e.target.value}))}
            />
            <TextField
              label="Body (En) — Vars: {{horse_name}} {{procedures}} {{date}} {{notes}}"
              value={editing?.body_en || ''}
              onChange={(e)=>setEditing(s=>({...s, body_en: e.target.value}))}
              multiline minRows={4}
            />

            <Typography variant="subtitle2">معاينة سريعة</Typography>
            <Paper variant="outlined" sx={{ p:1.5 }}>
              <Typography variant="overline">AR</Typography>
              <Typography variant="subtitle2">{applyVars(editing?.title_ar, sampleVars) || '—'}</Typography>
              <Typography variant="body2" sx={{ whiteSpace:'pre-wrap' }}>
                {applyVars(editing?.body_ar, sampleVars) || '—'}
              </Typography>
              <Box sx={{ height: 8 }}/>
              <Typography variant="overline">EN</Typography>
              <Typography variant="subtitle2">{applyVars(editing?.title_en, sampleVars) || '—'}</Typography>
              <Typography variant="body2" sx={{ whiteSpace:'pre-wrap' }}>
                {applyVars(editing?.body_en, sampleVars) || '—'}
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDlgOpen(false)}>إلغاء</Button>
          <Button variant="contained" onClick={save}>حفظ</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} onClose={onClose} autoHideDuration={2500}>
        <Alert severity={snack.sev} onClose={onClose}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
